import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CreateAnalysisYear, DeleteAnalysisYear } from '../../../API/AnalysisAPI';
import { PutValueAvailability } from '../../../API/AvailabilityAPI';
import { PutValueEbtida } from '../../../API/EbtidaAPI';
import { PutValueIndebtedness } from '../../../API/IndebtednessAPI';
import useModal from '../../../Hooks/useModal';
import { IResponseProps } from '../../../Interfaces/IResponseProps';
import { AvailabilityModel } from '../../../Models/AvailabilityModel';
import { EbtidaModel } from '../../../Models/EbtidaModel';
import { IndebtednessModel } from '../../../Models/IndebtednessModel';
import { AvailabilityColumns, EbtidaColumns, IndebtednessColumns } from '../../../Static/AnalysisColumns';
import CustomTableComponent from '../../Shared/CustomTable';
import ModalSelectYear from '../../Shared/ModalSelectYear';
import PaginationTabHeader from '../../Shared/PaginationTabHeader';
import { Button } from 'antd';

interface ITableCreateAnalysisProps {
    rowsIndebtedness: IndebtednessModel[],
    rowsEbtida: EbtidaModel[],
    rowsAvailability: AvailabilityModel[],
    companyId: string,
    userEmail: string,
    yearsCreated: number[],
    toggleLoading: (data: boolean) => void,
    setRowsIndebtedness: React.Dispatch<React.SetStateAction<IndebtednessModel[]>>,
    setRowsAvailability: React.Dispatch<React.SetStateAction<AvailabilityModel[]>>,
    setRowsEbtida: React.Dispatch<React.SetStateAction<EbtidaModel[]>>,
    setValueHasChanged: React.Dispatch<React.SetStateAction<boolean>>,
    canNotView: boolean,
    currentVersion: number,
    lastVersion: number,
    hasConcluded: boolean
}

const TableCreateAnalysis = (props: ITableCreateAnalysisProps) => {
    const { showModal, toggleShowModal } = useModal();
    const [pag, setPag] = useState(0);
    const [key, setKey] = useState(0);
    const [year, setYear] = useState("");
    const [isCreate, setIsCreate] = useState(true);

    const CalcResult = (id: string, value: string, info: string) => {
        if (pag === 0) {
            props.setRowsEbtida((current: EbtidaModel[]) =>
                current.map((ebtida: any) => {
                    if (ebtida.id === id) {
                        ebtida[info] = parseFloat(value);
                        var result = ebtida.profitBeforeIRCSLL - ebtida.financialExpenses - ebtida.financialIncome - ebtida.depreciationPostedDRE;
                        if(result != ebtida.ebtidaResult) {
                            props.setValueHasChanged(true);
                        }
                        return { ...ebtida, ebtidaResult: result };
                    }
                    return ebtida;
                }),
            );
            setKey(key + 1);
        } else if (pag === 1) {
            props.setRowsIndebtedness((current: IndebtednessModel[]) =>
                current.map((indebtedness: any) => {
                    if (indebtedness.id === id) {
                        indebtedness[info] = parseFloat(value);
                        var result = indebtedness.shortTermIndebtedness + indebtedness.longTermIndebtedness;
                        if(result != indebtedness.indebtednessResult) {
                            props.setValueHasChanged(true);
                        }
                        return { ...indebtedness, indebtednessResult: result };
                    }
                    return indebtedness;
                }),
            );
            setKey(key + 1);
        } else {
            props.setRowsAvailability((current: AvailabilityModel[]) =>
                current.map((availability: any) => {
                    if (availability.id === id) {
                        availability[info] = parseFloat(value);
                        var result = availability.cashAndEquivalents + availability.billsToReceive + availability.stock + availability.netFixedAssets + availability.financialInvestments;
                        if(result != availability.availabilityResult) {
                            props.setValueHasChanged(true);
                        }
                        return { ...availability, availabilityResult: result };
                    }
                    return availability;
                }),
            );
            setKey(key + 1);
        }
    }

    const createRow = async () => {
        try {
            props.toggleLoading(true);
            await CreateAnalysisYear(year, props.companyId, props.userEmail, props.currentVersion).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    setYear("");
                    props.setValueHasChanged(true);
                }
            }).finally(() => {
                props.toggleLoading(false);
            });
        } catch (error: any) {
            toast.error(error);
            props.toggleLoading(false);
        }
    }

    const deleteRow = async () => {
        try {
            props.toggleLoading(true);
            await DeleteAnalysisYear(year, props.companyId, props.currentVersion).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    setYear("");
                    props.setValueHasChanged(true);
                }
            }).finally(() => {
                props.toggleLoading(false);
            });
        } catch (error: any) {
            props.toggleLoading(false);
            toast.error(error);
        }
    }

    const setValueToInput = async (idLine: string, value: string, idColumn: string) => {
        if (idLine == "" || value == "" || idColumn == "" || value == undefined) {
            return;
        }
        try {
            props.toggleLoading(true);
            if (pag === 0) {
                await PutValueEbtida(idColumn, parseFloat(value), props.userEmail, idLine, props.companyId).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        CalcResult(idColumn, value, idLine);
                        if (idLine === "grossRevenue") {
                            props.setValueHasChanged(true);
                        }
                    }
                }).finally(() => {
                    props.toggleLoading(false);
                });
            } else if (pag === 1) {
                await PutValueIndebtedness(idColumn, parseFloat(value), props.userEmail, idLine, props.companyId).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        CalcResult(idColumn, value, idLine);
                        if (idLine === "netWorth" || idLine === "shortTermIndebtedness" || idLine === "totalAssets") {
                            props.setValueHasChanged(true);
                        }
                    }
                }).finally(() => {
                    props.toggleLoading(false);
                });
            } else {
                await PutValueAvailability(idColumn, parseFloat(value), props.userEmail, idLine, props.companyId).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        CalcResult(idColumn, value, idLine);
                    }
                }).finally(() => {
                    props.toggleLoading(false);
                });
            }
        } catch (error: any) {
            props.toggleLoading(false);
            toast.error(error.message);
        }
    }

    useEffect(() => {
        if (year != "") {
            if (isCreate) {
                createRow();
            } else {
                deleteRow();
            }
        }
    }, [year]);

    return (
        <div className='pagination-tabs mt-5'>
            <ModalSelectYear
                qtdYearsMinus={3}
                years={props.yearsCreated}
                isCreate={isCreate}
                setYear={setYear}
                show={showModal}
                toggleShow={toggleShowModal}
            />
                <div  className="d-flex w-100 align-items-center justify-content-end">
                {(!props.hasConcluded && !props.canNotView) &&
                    <div>
                        <Button  className='btn btn-sm btn-primary button-margin' onClick={() => { setIsCreate(true); toggleShowModal() }}>Acrescentar Ano de Análise</Button>
                        <Button  className='btn btn-sm btn-secondary button-margin ms-2' onClick={() => { setIsCreate(false); toggleShowModal() }}>Remover Ano de Análise</Button>
                    </div>
                }
                </div>
            <div className='d-flex align-items-center justify-content-between'>
                <PaginationTabHeader setPag={setPag} pag={pag} setKey={setKey} keyDefault={key} tabs={["Ebitda", "Total de Endividamento", "Total de Disponibilidade"]} />
               
            </div>
            <div className='pagination-tab-body p-3'>
                <CustomTableComponent
                    key={pag}
                    rows={pag === 0 ? props.rowsEbtida : pag === 1 ? props.rowsIndebtedness : props.rowsAvailability}
                    columns={pag === 0 ? EbtidaColumns : pag === 1 ? IndebtednessColumns : AvailabilityColumns}
                    isTableInverted={true}
                    isEditable={!props.hasConcluded}
                    functionToEdit={setValueToInput}
                    lastRowIsEditable={false}
                    hasHeader={true}
                    canNotEdit={props.canNotView}
                />
            </div>
        </div>
    )
}

export default TableCreateAnalysis;